import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './User.module.css';
import PastTrainings from './PastTrainings';
import { getAuthToken, useFetchWithAuth } from '../lib/auth';
import { HOST } from '../config/api';
import generateTrainingPDF from '../utils/pdfCreator';
import { getExerciseCategoryLabel } from '../utils/exerciseCategoryLabels';

const User = ({ userId, demoMode = false, demoData = null }) => {
  const [exercises, setExercises] = useState([]);
  const [exerciseComments, setExerciseComments] = useState([]);
  const [expandedCommentIndexes, setExpandedCommentIndexes] = useState([]);
  const [trainingName, setTrainingName] = useState('');
  const [trainingId, setTrainingId] = useState(undefined);
  const [trainingSaved, setTrainingSaved] = useState(false);
  const [flashingRowIndexes, setFlashingRowIndexes] = useState([]);
  const [refreshHistory, setRefreshHistory] = useState(false);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [failedExercises, setFailedExercises] = useState([]);
  const [trainingAlreadyCompleted, setTrainingAlreadyCompleted] = useState(false);
  const [hasNoUpcomingTraining, setHasNoUpcomingTraining] = useState(false);
  const [hasCoachSubscription, setHasCoachSubscription] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [submissionWarning, setSubmissionWarning] = useState('');

  const navigate = useNavigate();

  const fetchUserCoachSubscription = useFetchWithAuth(HOST + `/v1/user_coach_subscription?userid=${userId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
    },
  });

  const fetchTraining = useFetchWithAuth(HOST + `/v1/training?userid=${userId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
    },
  });

  const fetchTrainingHistory = useFetchWithAuth(HOST + `/v1/training_history?userid=${userId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
    },
  });

  useEffect(() => {
    if (!demoMode) {
      return;
    }

    const nextTraining = demoData?.nextTraining;

    setHasCoachSubscription(Boolean(demoData?.hasCoachSubscription));
    setHistoryData(demoData?.history || []);

    if (!nextTraining) {
      setTrainingId(undefined);
      setExercises([]);
      setTrainingName('');
      setCompletedExercises([]);
      setFailedExercises([]);
      setExerciseComments([]);
      setExpandedCommentIndexes([]);
      setTrainingAlreadyCompleted(false);
      setTrainingSaved(false);
      setHasNoUpcomingTraining(true);
      return;
    }

    setTrainingId(nextTraining.id);
    setExercises(nextTraining.exercises || []);
    setTrainingName(nextTraining.name || '');
    setCompletedExercises(Array((nextTraining.exercises || []).length).fill(false));
    setFailedExercises(Array((nextTraining.exercises || []).length).fill(false));
    setExerciseComments(Array((nextTraining.exercises || []).length).fill(''));
    setExpandedCommentIndexes([]);
    setTrainingAlreadyCompleted(false);
    setTrainingSaved(false);
    setSubmissionWarning('');
    setHasNoUpcomingTraining(false);
  }, [demoData, demoMode]);

  useEffect(() => {
    if (demoMode) {
      return;
    }

    const fetchData = async () => {
      try {
        const data = await fetchUserCoachSubscription();

        if (data?.status === 'success') {
          setHasCoachSubscription(data.subCount > 0);
        }
      } catch (error) {
        console.error('Failed to load coach subscription status', error);
      }
    };

    fetchData();
  }, [demoMode, fetchUserCoachSubscription, userId]);

  useEffect(() => {
    if (demoMode) {
      return;
    }

    const fetchData = async () => {
      try {
        const data = await fetchTraining();

        if (data?.status !== 'success') {
          return;
        }

        if (!data.lastTraining || data.lastTraining.completed === 1) {
          setTrainingId(undefined);
          setExercises([]);
          setTrainingName('');
          setCompletedExercises([]);
          setFailedExercises([]);
          setExerciseComments([]);
          setExpandedCommentIndexes([]);
          setTrainingAlreadyCompleted(false);
          setTrainingSaved(false);
          setSubmissionWarning('');
          setHasNoUpcomingTraining(true);
          return;
        }

        const recentTraining = data.lastTraining;

        setTrainingId(recentTraining.id);
        setExercises(recentTraining.exercises || []);
        setTrainingName(recentTraining.name || '');
        setCompletedExercises(Array((recentTraining.exercises || []).length).fill(false));
        setFailedExercises(Array((recentTraining.exercises || []).length).fill(false));
        setExerciseComments(Array((recentTraining.exercises || []).length).fill(''));
        setExpandedCommentIndexes([]);
        setTrainingAlreadyCompleted(false);
        setSubmissionWarning('');
        setHasNoUpcomingTraining(false);
      } catch (error) {
        console.error('Failed to load next training', error);
      }
    };

    fetchData();
  }, [demoMode, fetchTraining, userId]);

  useEffect(() => {
    if (demoMode) {
      return;
    }

    const fetchData = async () => {
      try {
        const data = await fetchTrainingHistory();

        if (data?.training) {
          setHistoryData(data.training);
        }
      } catch (error) {
        console.error('Failed to load training history', error);
      }
    };

    fetchData();
  }, [demoMode, fetchTrainingHistory, refreshHistory, userId]);

  const toggleCompletion = (index) => {
    setCompletedExercises((prevState) => {
      const newCompletedExercises = [...prevState];
      newCompletedExercises[index] = !newCompletedExercises[index];
      return newCompletedExercises;
    });
  };

  const toggleFailed = (index) => {
    setFailedExercises((prevState) => {
      const newFailedExercises = [...prevState];
      newFailedExercises[index] = !newFailedExercises[index];
      return newFailedExercises;
    });
  };

  const handleToggleCompletion = (index) => {
    setSubmissionWarning('');
    if (failedExercises[index]) {
      toggleFailed(index);
    }
    toggleCompletion(index);
  };

  const handleToggleFailed = (index) => {
    setSubmissionWarning('');
    if (completedExercises[index]) {
      toggleCompletion(index);
    }
    toggleFailed(index);
  };

  const downloadPDF = () => {
    generateTrainingPDF(trainingName, exercises);
  };

  const toggleCommentEditor = (index) => {
    setExpandedCommentIndexes((prevState) =>
      prevState.includes(index)
        ? prevState.filter((item) => item !== index)
        : [...prevState, index]
    );
  };

  const handleCommentChange = (index, value) => {
    const normalizedValue = value.slice(0, 255);
    setExerciseComments((prevState) => {
      const nextComments = [...prevState];
      nextComments[index] = normalizedValue;
      return nextComments;
    });
  };

  const handleCompleteTrainingClick = async () => {
    const rowsToFlash = exercises.reduce((acc, exercise, index) => {
      if (!completedExercises[index] && !failedExercises[index]) {
        acc.push(index);
      }
      return acc;
    }, []);

    if (rowsToFlash.length > 0) {
      setSubmissionWarning('Please mark every exercise as completed or failed before finishing the training.');
      setFlashingRowIndexes(rowsToFlash);
      setTimeout(() => {
        setFlashingRowIndexes([]);
      }, 500);
      return;
    }

    if (demoMode) {
      setSubmissionWarning('');
      const newHistoryEntry = {
        timestamp: new Date().toISOString(),
        name: trainingName,
        exerciseHistoryModels: exercises.map((exercise, index) => ({
          category: exercise.category,
          description: exercise.description,
          completed: completedExercises[index] ? 1 : 0,
          comment: exerciseComments[index]?.trim() || null,
        })),
      };

      setHistoryData((prevState) => [newHistoryEntry, ...prevState]);
      setTrainingSaved(true);
      setHasNoUpcomingTraining(false);
      setRefreshHistory((prev) => !prev);
      return;
    }

    const currentDateTime = new Date().toISOString();

    const newPayload = {
      userId,
      timestamp: currentDateTime,
      trainingId,
      completedTrainingExercises: exercises.map((exercise, index) => ({
        id: exercise.id,
        state: completedExercises[index] ? 'completed' : 'failed',
        comment: exerciseComments[index]?.trim() || '',
      })),
    };

    try {
      setSubmissionWarning('');
      const response = await fetch(HOST + '/v1/complete_training', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(newPayload),
      });

      if (response.status === 401) {
        console.error('Unauthorized: Redirecting to login page');
        navigate('/login');
        return;
      }

      if (response.ok) {
        setTrainingSaved(true);
        setHasNoUpcomingTraining(false);
        setRefreshHistory((prev) => !prev);
      } else {
        console.error('Failed to save training:', response.statusText);
      }
    } catch (error) {
      console.error('Error completing training:', error);
    }
  };

  const completedCount = useMemo(
    () => completedExercises.filter(Boolean).length,
    [completedExercises]
  );

  const failedCount = useMemo(
    () => failedExercises.filter(Boolean).length,
    [failedExercises]
  );

  const pendingCount = useMemo(
    () => exercises.length - completedCount - failedCount,
    [completedCount, exercises.length, failedCount]
  );

  const lastSession = historyData[0];
  const lastSessionCompleted = lastSession
    ? lastSession.exerciseHistoryModels.filter((exercise) => exercise.completed === 1).length
    : 0;
  const lastSessionTotal = lastSession?.exerciseHistoryModels?.length || 0;

  return (
    <div className={styles.userContainer}>
      <section className={styles.heroCard}>
        <div>
          <span className={styles.kicker}>Your training</span>
          <h2>Stay close to the next session.</h2>
          <p>
            Mark each exercise as completed or failed, finish the session with confidence, and keep a readable record of your recent work.
          </p>
        </div>
      </section>

      {!hasCoachSubscription ? (
        <section className={styles.emptyState}>
          <span className={styles.kicker}>No active coach yet</span>
          <h3>Subscribe to a training plan to unlock your training workspace.</h3>
          <p>
            After you subscribe, your coach&apos;s assigned sessions will appear here automatically and this page becomes your day-to-day training workspace.
          </p>
        </section>
      ) : (
        <>
          <section className={styles.statsGrid}>
            <article className={styles.statCard}>
              <span>Exercises in this session</span>
              <strong>{exercises.length}</strong>
              <small>Today&apos;s training workload</small>
            </article>
            <article className={styles.statCard}>
              <span>Marked completed</span>
              <strong>{completedCount}</strong>
              <small>Exercises you have finished so far</small>
            </article>
            <article className={styles.statCard}>
              <span>Pending</span>
              <strong>{pendingCount < 0 ? 0 : pendingCount}</strong>
              <small>Still waiting for a result</small>
            </article>
          </section>

          <section className={styles.trainingGrid}>
            <article className={`${styles.panel} ${styles.panelWide}`}>
              <div className={styles.panelHeader}>
                <div>
                  <span className={styles.sectionLabel}>Next training</span>
                  <h3>{trainingName || 'No training assigned yet'}</h3>
                  <p>
                    {hasNoUpcomingTraining
                      ? 'Your active plan is ready, but your coach has not assigned your next session yet.'
                      : 'Work through each exercise below. Every row should end with either completed or failed before you submit the session.'}
                  </p>
                </div>
              </div>

              {trainingSaved || trainingAlreadyCompleted ? (
                <div className={styles.completionState}>
                  <strong>Training completed. Good job.</strong>
                  <p>
                    Your result has been recorded and added to your recent history below.
                  </p>
                </div>
              ) : hasNoUpcomingTraining ? (
                <div className={styles.summaryCard}>
                  <span>Current status</span>
                  <strong>No upcoming training</strong>
                  <small>Check back soon or contact your coach if you were expecting a session.</small>
                </div>
              ) : (
                <>
                  <div className={styles.exerciseList}>
                    {exercises.map((exercise, index) => {
                      const isCompleted = completedExercises[index];
                      const isFailed = failedExercises[index];

                      return (
                        <article
                          key={exercise.id}
                          className={`${styles.exerciseCard} ${isCompleted ? styles.exerciseDone : ''} ${isFailed ? styles.exerciseFailed : ''} ${flashingRowIndexes.includes(index) ? styles.flash : ''}`}
                        >
                          <div className={styles.exerciseTopRow}>
                            <div className={styles.exerciseIndex}>{index + 1}</div>
                            <div className={styles.exerciseMeta}>
                              <strong>{getExerciseCategoryLabel(exercise.category)}</strong>
                              <p>{exercise.description}</p>
                            </div>
                            <div className={`${styles.statusBadge} ${isCompleted ? styles.statusDone : isFailed ? styles.statusFailed : styles.statusPending}`}>
                              {isCompleted ? 'Completed' : isFailed ? 'Failed' : 'Pending'}
                            </div>
                          </div>

                          <div className={styles.exerciseActions}>
                            <button
                              className={`${styles.actionButton} ${styles.actionPrimary} ${isCompleted ? styles.actionSelected : ''}`}
                              onClick={() => !trainingSaved && handleToggleCompletion(index)}
                              disabled={trainingSaved}
                            >
                              {isCompleted ? 'Undo completed' : 'Mark completed'}
                            </button>
                            <button
                              className={`${styles.actionButton} ${styles.actionDanger} ${isFailed ? styles.actionSelectedDanger : ''}`}
                              onClick={() => !trainingSaved && handleToggleFailed(index)}
                              disabled={trainingSaved}
                            >
                              {isFailed ? 'Undo failed' : 'Mark failed'}
                            </button>
                            <button
                              className={`${styles.actionButton} ${styles.actionSecondary}`}
                              onClick={() => !trainingSaved && toggleCommentEditor(index)}
                              disabled={trainingSaved}
                            >
                              {expandedCommentIndexes.includes(index)
                                ? (exerciseComments[index] ? 'Hide comment' : 'Cancel comment')
                                : (exerciseComments[index] ? 'Edit comment' : 'Add comment')}
                            </button>
                          </div>

                          {expandedCommentIndexes.includes(index) ? (
                            <div className={styles.commentEditor}>
                              <label className={styles.commentLabel} htmlFor={`exercise-comment-${index}`}>
                                Exercise comment
                              </label>
                              <textarea
                                id={`exercise-comment-${index}`}
                                className={styles.commentInput}
                                maxLength={255}
                                value={exerciseComments[index] || ''}
                                onChange={(event) => handleCommentChange(index, event.target.value)}
                                placeholder="Add a quick note for your coach..."
                                disabled={trainingSaved}
                              />
                              <span className={styles.commentHint}>
                                {255 - (exerciseComments[index]?.length || 0)} characters remaining
                              </span>
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>

                  {submissionWarning ? (
                    <div className={styles.submissionWarning} role="alert">
                      {submissionWarning}
                    </div>
                  ) : null}

                  <div className={styles.panelActions}>
                    <button className={styles.completeTrainingButton} onClick={handleCompleteTrainingClick}>
                      Complete training
                    </button>
                    <button className={styles.downloadPDFButton} onClick={downloadPDF}>
                      Download PDF
                    </button>
                  </div>
                </>
              )}
            </article>

            <aside className={styles.panel}>
              <div className={styles.panelHeader}>
                <div>
                  <span className={styles.sectionLabel}>Session summary</span>
                  <h3>Keep the session clean.</h3>
                  <p>
                    This side panel helps you see whether anything is still missing before you submit.
                  </p>
                </div>
              </div>

              <div className={styles.summaryStack}>
                <div className={styles.summaryCard}>
                  <span>Completed</span>
                  <strong>{completedCount}</strong>
                </div>
                <div className={styles.summaryCard}>
                  <span>Failed</span>
                  <strong>{failedCount}</strong>
                </div>
                <div className={styles.summaryCard}>
                  <span>Still pending</span>
                  <strong>{pendingCount < 0 ? 0 : pendingCount}</strong>
                </div>
                <div className={styles.summaryCard}>
                  <span>Last recorded session</span>
                  <strong>{lastSession ? lastSession.name : 'No history yet'}</strong>
                  <small>
                    {lastSession
                      ? `${lastSessionCompleted}/${lastSessionTotal} exercises completed`
                      : 'Your latest finished training will appear here.'}
                  </small>
                </div>
              </div>
            </aside>
          </section>

          <PastTrainings
            userId={userId}
            refresh={refreshHistory}
            demoMode={demoMode}
            historyOverride={historyData}
          />
        </>
      )}
    </div>
  );
};

export default User;
